import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import { usePageMeta } from "@/hooks/usePageMeta";

const values = [
  { icon: "ri-scales-3-line", title: "理性与温度并重", desc: "在条理清晰的内容组织之外，保留对人性的理解与关怀。" },
  { icon: "ri-layout-grid-line", title: "清晰的浏览体验", desc: "分类、筛选与推荐层层递进，让找片变得轻松高效。" },
  { icon: "ri-shield-check-line", title: "尊重原创与版权", desc: "站内内容均为演示用途的虚构信息，尊重每一位创作者的权益。" },
];

const team = [
  { name: "内容策划组", role: "负责伦理影视题材的整理、分类与专题策划" },
  { name: "产品体验组", role: "打磨筛选、搜索与播放流程，提升浏览效率" },
  { name: "技术维护组", role: "保障站点稳定运行与移动端适配体验" },
];

export default function AboutPage() {
  usePageMeta({
    title: "关于我们 - 伦理影院",
    description: "了解伦理影院的平台定位、团队理念与联系方式。伦理影院是聚焦伦理题材影视剧的内容展示平台，站内信息均为演示用虚构内容。",
    keywords: "伦理影院,关于我们,伦理影视平台,影视内容站",
  });

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <section className="w-full border-b border-background-200/70 bg-background-100">
          <div className="w-full px-4 py-14 md:px-8 md:py-20">
            <h1 className="font-heading text-3xl font-bold text-foreground-950 md:text-4xl">关于伦理影院</h1>
            <p className="mt-5 max-w-3xl text-sm leading-7 text-foreground-700 md:text-base">
              伦理影院是一个专注于伦理题材影视剧的内容展示与浏览平台。我们希望为喜欢伦理电影的观众提供一个清晰、舒适、值得信赖的入口，
              让每一部打动人心的伦理故事都能被更容易地发现。
            </p>
          </div>
        </section>

        <div className="w-full px-4 md:px-8">
          <section className="pt-12">
            <h2 className="font-heading text-2xl font-bold text-foreground-950">我们的定位</h2>
            <p className="mt-4 max-w-3xl text-sm leading-7 text-foreground-700">
              我们聚焦伦理题材这一细分方向，围绕正义、家庭、都市、情感等议题，持续整理优质的伦理电影与电视剧资源。
              通过多维度筛选、主题专题与个性化推荐，帮助观众在短时间内定位到自己真正想看的作品。
            </p>
            <p className="mt-3 max-w-3xl text-sm leading-7 text-foreground-700">
              同时，我们坚持把「观影」本身做得更轻松——从浏览、筛选到详情与播放演示，每一步都力求直观、顺畅、友好。
            </p>
          </section>

          <section className="pt-12">
            <h2 className="font-heading text-2xl font-bold text-foreground-950">我们的理念</h2>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3 md:gap-5">
              {values.map((v) => (
                <div key={v.title} className="rounded-lg border border-background-200/70 bg-background-100 p-5">
                  <span className="flex h-12 w-12 items-center justify-center rounded-md bg-primary-500 text-background-50">
                    <i className={`${v.icon} text-xl`}></i>
                  </span>
                  <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">{v.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{v.desc}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="pt-12">
            <h2 className="font-heading text-2xl font-bold text-foreground-950">维护团队</h2>
            <p className="mt-4 max-w-3xl text-sm leading-7 text-foreground-700">
              伦理影院由一支小而专注的团队共同维护，成员来自内容、产品与技术等不同方向，因对伦理题材影视的热爱走到一起。
            </p>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3 md:gap-5">
              {team.map((t) => (
                <div key={t.name} className="rounded-lg border border-background-200/70 bg-background-50 p-5">
                  <h3 className="font-heading text-base font-semibold text-foreground-950">{t.name}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{t.role}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="pt-12">
            <h2 className="font-heading text-2xl font-bold text-foreground-950">联系方式</h2>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5">
              <div className="flex items-center gap-4 rounded-lg border border-background-200/70 bg-background-100 p-5">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-accent-500 text-primary-950">
                  <i className="ri-mail-line text-xl"></i>
                </span>
                <div className="min-w-0">
                  <p className="text-xs text-foreground-500">联系邮箱</p>
                  <a
                    href="mailto:contact@ethcinema.example.com"
                    className="break-all text-sm font-medium text-foreground-900 transition-colors hover:text-primary-600"
                  >
                    contact@ethcinema.example.com
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-4 rounded-lg border border-background-200/70 bg-background-100 p-5">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-secondary-500 text-background-50">
                  <i className="ri-share-circle-line text-xl"></i>
                </span>
                <div className="min-w-0">
                  <p className="text-xs text-foreground-500">社交媒体</p>
                  <div className="mt-1 flex flex-wrap gap-3 text-sm text-foreground-900">
                    <a href="https://weibo.com" target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-primary-600">
                      微博
                    </a>
                    <a href="https://www.douban.com" target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-primary-600">
                      豆瓣
                    </a>
                    <a href="https://www.bilibili.com" target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-primary-600">
                      哔哩哔哩
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="pt-12 pb-4">
            <h2 className="font-heading text-2xl font-bold text-foreground-950">版权与免责声明</h2>
            <div className="mt-4 rounded-lg border border-background-200/70 bg-background-100 p-5 md:p-6">
              <p className="text-sm leading-7 text-foreground-700">
                本站为演示性质的内容展示站点，站内所有影视作品的标题、剧情简介、导演、演员、评分等信息均为虚构，
                仅用于产品功能与界面展示，不代表任何真实作品或人物。
              </p>
              <p className="mt-3 text-sm leading-7 text-foreground-700">
                站内「在线播放」为纯前端模拟演示，不提供、也不接入任何真实音视频内容，不承担由此产生的任何责任。
                如本站内容无意中侵犯了您的合法权益，请通过上方邮箱与我们联系，我们将及时处理。
              </p>
            </div>
          </section>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}